/**
 * Shared atomic UI primitives for the SiteDetail tabs/panels.
 *
 * Kept tiny and dependency-free — these are the "Row, Stat, StatTile" of the admin
 * detail page. Splitting them out makes the per-tab files importable in isolation
 * without re-declaring layout helpers.
 */
import React from "react";

export function Row({ label, children }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-3 items-start">
      <div className="label-tiny pt-2.5">{label}</div>
      <div>{children}</div>
    </div>
  );
}

export function Stat({ label, value, sub }) {
  return (
    <div className="border border-zinc-800 rounded-sm p-5 bg-zinc-900/40">
      <div className="label-tiny">{label}</div>
      <div className="font-display text-3xl font-black mt-2 mono">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1 mono">{sub}</div>}
    </div>
  );
}

export function StatTile({ label, value, sub, accent = "zinc" }) {
  const accentColors = {
    emerald: "text-emerald-400 border-emerald-500/20",
    amber:   "text-amber-400 border-amber-500/20",
    indigo:  "text-indigo-400 border-indigo-500/20",
    zinc:    "text-zinc-300 border-zinc-800",
  };
  return (
    <div className={`border bg-zinc-900/40 rounded-sm p-3 ${accentColors[accent].split(" ")[1]}`}>
      <div className="label-tiny">{label}</div>
      <div className={`font-display font-bold text-2xl mt-1 ${accentColors[accent].split(" ")[0]}`}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-500 mono mt-1">{sub}</div>}
    </div>
  );
}

/**
 * Compact stat used inside grid rows in IndexNow / CF Analytics / Bluesky panels.
 * Smaller than StatTile (no border).
 */
export function IndexNowStat({ label, value, tone, testid }) {
  const toneCls =
    tone === "ok" ? "text-emerald-300" :
    tone === "warn" ? "text-amber-300" :
    "text-zinc-200";
  return (
    <div data-testid={testid}>
      <div className="label-tiny text-[10px]">{label}</div>
      <div className={`mono text-sm ${toneCls}`}>{value}</div>
    </div>
  );
}
